import { useState, useMemo } from 'react';

export interface UseTableSortReturn<T> {
  sortedData: T[];
  paginatedData: T[];
  sortField: string | null;
  sortDirection: 'asc' | 'desc';
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  handleSort: (field: string) => void;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (count: number) => void;
}

export function useTableSort<T>(
  data: T[],
  defaultSortField: string | null = null,
  defaultItemsPerPage: number = 10
): UseTableSortReturn<T> {
  const [sortField, setSortField] = useState<string | null>(defaultSortField);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortField) return data;

    return [...data].sort((a: any, b: any) => {
      // Handle nested fields (e.g., 'customer.name')
      const getNestedValue = (obj: any, path: string) => {
        return path.split('.').reduce((current, key) => current?.[key], obj);
      };

      const aValue = getNestedValue(a, sortField);
      const bValue = getNestedValue(b, sortField);

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Boolean comparison
      if (typeof aValue === 'boolean') {
        const result = aValue === bValue ? 0 : aValue ? -1 : 1;
        return sortDirection === 'asc' ? result : -result;
      }

      // String comparison (case-insensitive)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const result = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        return sortDirection === 'asc' ? result : -result;
      }

      // Number comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        const result = aValue - bValue;
        return sortDirection === 'asc' ? result : -result;
      }

      // Date comparison
      if (aValue instanceof Date && bValue instanceof Date) {
        const result = aValue.getTime() - bValue.getTime();
        return sortDirection === 'asc' ? result : -result;
      }

      // Array length comparison (for counting)
      if (Array.isArray(aValue) && Array.isArray(bValue)) {
        const result = aValue.length - bValue.length;
        return sortDirection === 'asc' ? result : -result;
      }

      // Default comparison
      const result = String(aValue).localeCompare(String(bValue));
      return sortDirection === 'asc' ? result : -result;
    });
  }, [data, sortField, sortDirection]);

  // Calculate pagination
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, itemsPerPage]);

  // Handle sort
  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (count: number) => {
    setItemsPerPage(count);
    setCurrentPage(1); // Reset to first page
  };

  return {
    sortedData,
    paginatedData,
    sortField,
    sortDirection,
    currentPage,
    totalPages,
    itemsPerPage,
    handleSort,
    setCurrentPage,
    setItemsPerPage: handleItemsPerPageChange,
  };
}

